import { faPlus, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, TextField, Box, Alert, Typography, Grid } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { isAfter } from "date-fns";
import { isEqual } from "lodash";
import React, { useState, useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import CustomConfirmationChangesDialog from "src/containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { FieldsContainer, AddEditButton } from "./styles";
import useAuthorization from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { EmployeesFilter } from "../../../../models/employees";
import { TnAEmployeesAnomaliesFilter } from "../../../../models/tna-employee-anomalies";
import { TnATimesheetConfirmationsDTO } from "../../../../models/tna-timesheet-confirmation";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";

import { CompanyLegalEntitiesApiService } from "../../../../services/company-legal-entities/company-legal-entities-api.service";

import { EmployeeAnomaliesQueryApiService } from "../../../../services/employee-anomalies-query/employee-anomalies-query-api.service";
import { EmployeesApiService } from "../../../../services/employees/employees-api.service";
import { TnATimesheetConfirmationsApiService } from "../../../../services/tna-timesheet-confirmations/tna-timesheet-confirmations-api.service";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";
import ConfirmationChangesDialog from "../../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../DialogActions/DialogActions";
import DialogTitle from "../../../DialogTitle/DialogTitle";

import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";

export const AddEditTimesheetConfirmationRequest = ({
  effect = "add",
  asLink = false,
  linkText,
  item,
  defaultLegalEntity = null,
  handleNotifyParent,
  children,
  readOnly,
  customLinkStyle,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_time_sheet_confirmation"; // tRP - translation root path
  const translationFields = `${tRP}.fields`;

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const DEFAULT_ITEM: Partial<TnATimesheetConfirmationsDTO> = useMemo(() => {
    return {
      companyAccount: item?.companyAccount,
      legalEntity: defaultLegalEntity ?? item?.legalEntity ?? null,
      employee: item?.employee ?? null,
      asOfDate: CustomDate.FromDate(new Date()),
    };
  }, [defaultLegalEntity, item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      signOffRequest: {
        ...(item ?? (DEFAULT_ITEM as TnATimesheetConfirmationsDTO)),
        asOfDate: CustomDate.FromDate(new Date()),
      },
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
      isWarningCCCDOpen: false,
      timesheetAnomalies: 0,
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeField = useCallback(
    (
      field: keyof TnATimesheetConfirmationsDTO,
      value: TnATimesheetConfirmationsDTO[keyof TnATimesheetConfirmationsDTO]
    ) => {
      setState((state) => ({
        ...state,
        signOffRequest: {
          ...state.signOffRequest,
          [field]: value,
        },
      }));
    },
    []
  );

  const checkTimesheetAnomaliesAndSave = useCallback(async () => {
    setState((state) => ({ ...state, error: "", loading: true }));

    try {
      const timesheetAnomaliesResponse = await new EmployeeAnomaliesQueryApiService().loadItems({
        tnAEmployeeAnomalies: [
          {
            employeeID: state.signOffRequest?.employee?.id as string,
            fromDate: state.signOffRequest?.fromDate
              ? `${state.signOffRequest?.fromDate?.year}-${
                  state.signOffRequest?.fromDate?.month < 10
                    ? `0${state.signOffRequest?.fromDate?.month}`
                    : state.signOffRequest?.fromDate?.month
                }-${
                  state.signOffRequest?.fromDate?.day < 10
                    ? `0${state.signOffRequest?.fromDate?.day}`
                    : state.signOffRequest?.fromDate?.day
                }`
              : null,
          },
        ],
        asOfDate: state.signOffRequest?.asOfDate
          ? `${state.signOffRequest?.asOfDate?.year}-${
              state.signOffRequest?.asOfDate?.month < 10
                ? `0${state.signOffRequest?.asOfDate?.month}`
                : state.signOffRequest?.asOfDate?.month
            }-${
              state.signOffRequest?.asOfDate?.day < 10
                ? `0${state.signOffRequest?.asOfDate?.day}`
                : state.signOffRequest?.asOfDate?.day
            }`
          : null,
      } as TnAEmployeesAnomaliesFilter);

      if (!timesheetAnomaliesResponse.error) {
        if (timesheetAnomaliesResponse.items[0]?.anomalies) {
          setState((state) => ({
            ...state,
            isWarningCCCDOpen: true,
            timesheetAnomalies: timesheetAnomaliesResponse.items[0].anomalies,
          }));
        } else {
          const response = await new TnATimesheetConfirmationsApiService().saveItems([
            {
              ...state.signOffRequest,
              ...(effect === "add" && { id: uuidv4() }),
            } as TnATimesheetConfirmationsDTO,
          ]);

          if (!response.error) {
            if (effect === "add") {
              setState({ ...INITIAL_STATE });
            } else if (effect === "edit") {
              setState((state) => ({
                ...state,
                isDialogOpen: false,
                isCCDOpen: false,
              }));
            }
            handleNotifyParent();
          } else {
            setState((state) => ({
              ...state,
              error: response.error,
            }));
          }

          toastifyResponse(response);
        }
      } else {
        setState((state) => ({
          ...state,
          error: timesheetAnomaliesResponse.error,
        }));
      }

      toastifyResponse(timesheetAnomaliesResponse, "failed");
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
      handleNotifyParent();
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [
    state.isWarningCCCDOpen,
    state.signOffRequest.employee,
    state.signOffRequest.asOfDate,
    state.signOffRequest.fromDate,
    INITIAL_STATE,
    effect,
    handleNotifyParent,
    state.signOffRequest,
  ]);

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, error: "", loading: true }));

    try {
      const response = await new TnATimesheetConfirmationsApiService().saveItems([
        {
          ...state.signOffRequest,
          ...(effect === "add" && { id: uuidv4() }),
        } as TnATimesheetConfirmationsDTO,
      ]);

      if (!response.error) {
        if (effect === "add") {
          setState({ ...INITIAL_STATE });
        } else if (effect === "edit") {
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
            isWarningCCCDOpen: false,
          }));
        }
        handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
          loading: false,
        }));
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
        loading: false,
      }));
      handleNotifyParent();
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.signOffRequest]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.signOffRequest, INITIAL_STATE.signOffRequest)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return (
      hasAccess &&
      !!state.signOffRequest?.legalEntity &&
      !!state.signOffRequest?.employee &&
      !!state.signOffRequest?.asOfDate &&
      !isAfter(CustomDate.ToDate(state.signOffRequest?.asOfDate) as Date, new Date()) &&
      (state.signOffRequest?.fromDate
        ? (CustomDate.ToDate(state.signOffRequest?.asOfDate) ?? new Date()) >
          (CustomDate.ToDate(state.signOffRequest?.fromDate) ?? new Date())
        : true)
    );
  }, [state.signOffRequest?.legalEntity, state.signOffRequest?.employee, state.signOffRequest?.asOfDate]);

  return (
    <>
      <AddEditButton
        title={`${t(`${tRP}.request_signOff`)}`}
        effect={effect}
        asLink={asLink}
        onClick={() => {
          !readOnly && setState((state) => ({ ...state, isDialogOpen: true }));
        }}
        variant={effect === "add" ? "outlined" : "text"}
        {...(asLink && { style: customLinkStyle })}
      >
        {children ? (
          children
        ) : (
          <>
            {asLink ? (
              linkText
            ) : effect === "add" ? (
              <FontAwesomeIcon icon={faPlus} />
            ) : effect === "edit" ? (
              <FontAwesomeIcon icon={faPencilAlt} />
            ) : (
              <></>
            )}
          </>
        )}
      </AddEditButton>

      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={effect === "add" ? t(`${tRP}.title_for_add_component`) : t(`${tRP}.title_for_edit_component`)}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <FieldsContainer>
              <Box sx={{ topMargin: 5, padding: 5 }}>
                <AutocompleteDropdown
                  labelKey={t("common_words.legal_entity")}
                  field={"legalEntity"}
                  serviceApi={new CompanyLegalEntitiesApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.signOffRequest.legalEntity ?? null}
                  disabled={!!state.signOffRequest.legalEntity}
                />
                <AutocompleteDropdown
                  labelKey={t("common_words.employee")}
                  field={"employee"}
                  serviceApi={new EmployeesApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.signOffRequest.employee}
                  propsFilter={
                    {
                      legalEntityIds: [state.signOffRequest?.legalEntity?.id],
                      requiresTEStartDate: true,
                    } as EmployeesFilter
                  }
                  disabled={!!state.signOffRequest.employee}
                />
              </Box>
              <Box sx={{ topMargin: 5, padding: 5 }}>
                {!!state.signOffRequest?.signedAsOfDate && (
                  <DesktopDatePicker
                    inputFormat="dd.MM.yyyy"
                    mask="__.__.____"
                    label={`${t(`${tRP}.fields.signedAsOf_date`)}`}
                    value={CustomDate.ToDate(state.signOffRequest?.signedAsOfDate)}
                    onChange={(newValue) => {
                      /*DO NOT DO ANYTHING as we to not change here the fromDate*/
                    }}
                    renderInput={(params) => <TextField {...params} />}
                    disabled
                  />
                )}
                <DesktopDatePicker
                  inputFormat="dd.MM.yyyy"
                  mask="__.__.____"
                  label={`${t(`${tRP}.fields.asOf_date_r`)}`}
                  value={CustomDate.ToDate(state.signOffRequest?.asOfDate)}
                  onChange={(newValue) => {
                    handleChangeField("asOfDate", newValue ? CustomDate.FromDate(newValue) : null);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  minDate={item?.fromDate ? CustomDate.ToDate(item?.fromDate) : null}
                  disableFuture
                />
              </Box>
            </FieldsContainer>
            {state.error && <Alert severity="error">{state.error}</Alert>}
          </DialogContent>
          <DialogActions
            handleSave={checkTimesheetAnomaliesAndSave}
            disabledSaveButton={!areFieldsValidated()}
            handleCancel={resetAndCloseDialog}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />

      <CustomConfirmationChangesDialog
        open={state.isWarningCCCDOpen}
        loading={state.loading}
        title={`${t(`${tRP}.timesheet_has_anomalies`)}`}
        handleCancel={() => {
          setState((state) => ({
            ...state,
            isWarningCCCDOpen: false,
            timesheetAnomalies: 0,
          }));
        }}
        actionsAndHandlers={[
          {
            action: "Yes",
            buttonVariant: "contained",
            handler: () => {
              handleSave();
            },
          },
        ]}
      >
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Typography>
            <Trans
              i18nKey={`${tRP}.request_confimation_message`}
              values={{
                employeeName: state.signOffRequest.employee?.name ?? "",
                timesheetAnomalies: state.timesheetAnomalies,
              }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </Grid>
      </CustomConfirmationChangesDialog>
    </>
  );
};
